/**
 * Area `billing-account` of the console gateway — over `apps/api/src/routes/billing-account/` (design §27.9.2). Owned by plan-014
 * unit **D1b (then D3b, D4, D6)**.
 *
 * plan-014 D4 adds what Finance → Funding reads (design §27.15.1; FR12-BIL-04/06/10):
 *
 *   listBillingAccounts(accountId)   `GET /v1/accounts/{account_id}/billing-accounts` — every balance the person may read,
 *                                    the shared one first: its live figures (balance, Held, available), which workspaces
 *                                    it pays for and since when with their open usage, and this billing month's spend
 *
 * Organization finance always names the balance it reads (FR12-BIL-04): nothing here picks "the first" balance.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

/** A workspace a balance pays for, since when, and what it is doing now (seconds: the API's unit). */
export interface FundedWorkspaceView {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly since: string;
  readonly billingAssignmentId: string;
  readonly unbilledTodaySecond: number;
  readonly reservedSecond: number;
  readonly heldConversationCount: number;
}

/** One balance's live figures — the one computation every balance screen shows. */
export interface BalanceCreditView {
  readonly availableMinor: number;
  readonly balanceMinor: number;
  readonly unbilledTodayMinor: number;
  readonly reservedMinor: number;
  readonly heldConversationCount: number;
  readonly currency: string;
}

export interface BillingAccountSummaryView {
  readonly billingAccountId: string;
  readonly scope: "account" | "workspace";
  readonly fundedWorkspaceId: string | null;
  readonly displayName: string | null;
  readonly lifecycleState: "active" | "retiring" | "retired";
  readonly collectionPolicy: "prepaid" | "postpaid";
  readonly currency: string;
  readonly credit: BalanceCreditView;
  readonly fundedWorkspace: readonly FundedWorkspaceView[];
  readonly monthSpendMinor: number;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** Every balance of the organization the person may read, with its figures and the workspaces it pays for. */
    listBillingAccounts(accountId: string): Promise<readonly BillingAccountSummaryView[]>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type BillingAccountGatewaySlice = Pick<ConsoleGateway, "listBillingAccounts">;

/** This area's rows of `GATEWAY_GAP`: none — `listBillingAccounts` is in D1b's fragment. */
export const billingAccountGap: readonly GatewayGap[] = [];

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

const LIFECYCLE = ["active", "retiring", "retired"] as const;
const COLLECTION = ["prepaid", "postpaid"] as const;

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

/** One balance as the API sends it (`BillingAccountSummary`). Exported for the sandbox half's fixtures and suites. */
export function billingAccountSummaryOfWire(data: Readonly<Record<string, unknown>>, path: string): BillingAccountSummaryView {
  const account = readRecord(data["billing_account"], `${path}.billing_account`);
  const credit = readRecord(data["credit"], `${path}.credit`);
  const lifecycle = LIFECYCLE.find((one) => one === account["lifecycle_state"]);
  if (lifecycle === undefined) throw new Error(`${path}.billing_account.lifecycle_state is not a lifecycle state`);
  const collection = COLLECTION.find((one) => one === account["collection_policy"]);
  if (collection === undefined) throw new Error(`${path}.billing_account.collection_policy is not a collection policy`);
  return {
    billingAccountId: readText(account["billing_account_id"], `${path}.billing_account.billing_account_id`),
    scope: account["scope"] === "workspace" ? "workspace" : "account",
    fundedWorkspaceId: textOrNull(account["funded_workspace_id"], `${path}.billing_account.funded_workspace_id`),
    displayName: textOrNull(account["display_name"], `${path}.billing_account.display_name`),
    lifecycleState: lifecycle,
    collectionPolicy: collection,
    currency: readText(data["currency"], `${path}.currency`),
    credit: {
      availableMinor: readSigned(credit["available_minor"], `${path}.credit.available_minor`),
      balanceMinor: readSigned(credit["balance_minor"], `${path}.credit.balance_minor`),
      unbilledTodayMinor: readWholeNumber(credit["unbilled_today_minor"], `${path}.credit.unbilled_today_minor`),
      reservedMinor: readWholeNumber(credit["reserved_minor"], `${path}.credit.reserved_minor`),
      heldConversationCount: readWholeNumber(credit["held_conversation_count"], `${path}.credit.held_conversation_count`),
      currency: readText(credit["currency"], `${path}.credit.currency`),
    },
    fundedWorkspace: readArray(data["funded_workspace"], `${path}.funded_workspace`).map((raw, index) => {
      const one = readRecord(raw, `${path}.funded_workspace[${index}]`);
      const usage = readRecord(one["open_usage"], `${path}.funded_workspace[${index}].open_usage`);
      return {
        workspaceId: readText(one["workspace_id"], `${path}.funded_workspace.workspace_id`),
        workspaceName: readText(one["workspace_name"], `${path}.funded_workspace.workspace_name`),
        since: readText(one["since"], `${path}.funded_workspace.since`),
        billingAssignmentId: readText(one["billing_assignment_id"], `${path}.funded_workspace.billing_assignment_id`),
        unbilledTodaySecond: readWholeNumber(usage["unbilled_today_second"], `${path}.funded_workspace.open_usage.unbilled_today_second`),
        reservedSecond: readWholeNumber(usage["reserved_second"], `${path}.funded_workspace.open_usage.reserved_second`),
        heldConversationCount: readWholeNumber(usage["held_conversation_count"], `${path}.funded_workspace.open_usage.held_conversation_count`),
      };
    }),
    monthSpendMinor: readSigned(data["month_spend_minor"], `${path}.month_spend_minor`),
  };
}

/** A whole number that may be negative (a balance below zero is what happened to the money). */
function readSigned(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`${path} must be a whole number`);
  return value;
}

export function billingAccountApiSlice(client: WeTalkApiClient, _context: SliceContext): BillingAccountGatewaySlice {
  return {
    async listBillingAccounts(accountId) {
      const body = readRecord(assertOk(await client.listBillingAccounts({ path: { account_id: accountId } })), "listBillingAccounts");
      return readArray(body["data"], "listBillingAccounts.data").map((raw, index) =>
        billingAccountSummaryOfWire(readRecord(raw, `listBillingAccounts.data[${String(index)}]`), `listBillingAccounts.data[${String(index)}]`),
      );
    },
  };
}
